import React, { useState, useRef, useEffect } from 'react';
import { Text, Plane } from '@react-three/drei';
import * as THREE from 'three';

export default function ContactForm3D({ darkMode, opacity, position = [0, 0, 0], onActiveFieldChange }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeField, setActiveField] = useState(null);

  // Notify parent component when active field changes
  useEffect(() => {
    if (onActiveFieldChange) {
      onActiveFieldChange(activeField);
    }
  }, [activeField, onActiveFieldChange]);

  // Keyboard input handling
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!activeField || opacity < 0.1) return;
      
      if (e.key === 'Backspace') {
        setFormData(prev => ({
          ...prev,
          [activeField]: prev[activeField].slice(0, -1)
        }));
      } else if (e.key === 'Enter' && activeField === 'message') {
        handleSubmit();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        const fields = ['name', 'email', 'message'];
        const currentIndex = fields.indexOf(activeField);
        const nextIndex = (currentIndex + 1) % fields.length;
        setActiveField(fields[nextIndex]);
      } else if (e.key === 'Escape') {
        setActiveField(null);
      } else if (e.key.length === 1 && e.key.charCodeAt(0) >= 32) {
        // Only allow printable characters
        setFormData(prev => ({
          ...prev,
          [activeField]: prev[activeField] + e.key
        }));
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [activeField, opacity]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.message) return;
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://public.herotofu.com/v1/29202240-5451-11ee-99c9-b9cd803366c9', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openLinkedIn = () => {
    window.open('https://www.linkedin.com/in/naumanshah1220/', '_blank');
  };

  return (
    <group position={position} visible={opacity > 0}>
      {/* Background Panel */}
      <Plane 
        args={[12, 10]} 
        position={[0, 0, 0]}
        opacity={opacity}
      >
        <meshBasicMaterial 
          color={darkMode ? 0x1a1a1a:0x317ea1} 
          transparent 
          opacity={0.9}
        />
      </Plane>

      {/* Title */}
      <Text
        fontSize={0.6}
        color={darkMode ? 0xffffff : 0x134267}
        position={[0, 4.0, 0.1]}
        anchorX="center"
        anchorY="top"
        font="/Fonts/roc-grotesk-bold.otf"
        opacity={opacity}
      >
        get in touch
      </Text>

      {/* Underline for title */}
      <Plane 
        args={[3, 0.02]} 
        position={[0, 3.3, 0.1]} 
        opacity={opacity}
      >
        <meshBasicMaterial 
          color={darkMode ? 0xffffff : 0x134267} 
          transparent 
        />
      </Plane>

      {submitted ? (
        // Success message
        <group>
          <Text
            fontSize={0.4}
            color={darkMode ? 0xffffff : 0x134267}
            position={[0, 1, 0.1]}
            anchorX="center"
            anchorY="middle"
            font="/Fonts/roc-grotesk-bold.otf"
            opacity={opacity}
          >
            woohoo!
          </Text>
          <Text
            fontSize={0.3}
            color={darkMode ? 0xffffff : 0x134267}
            position={[0, 0.3, 0.1]}
            anchorX="center"
            anchorY="middle"
            font="/Fonts/roc-grotesk-regular.otf"
            opacity={opacity}
          >
            your message has been sent successfully.
          </Text>
        </group>
      ) : (
        // Form fields
        <group>
          {/* Name Field */}
          <Plane 
            args={[8, 0.8]} 
            position={[0, 1.5, 0.1]} 
            opacity={opacity}
            onClick={() => setActiveField('name')}
          >
            <meshBasicMaterial 
              color={activeField === 'name' ? (darkMode ? 0x000000 : 0xffffff) : (darkMode ? 0x333333 : 0xcbcbcb)} 
              transparent 
              opacity={0.8}
            />
          </Plane>
          
          {/* Name Field Border */}
          <Plane 
            args={[8.1, 0.9]} 
            position={[0, 1.5, 0.05]} 
            opacity={opacity}
          >
            <meshBasicMaterial 
              color={darkMode ? 0x666666 : 0x195787} 
              transparent 
              opacity={0.2}
            />
          </Plane>
          
          <Text
            fontSize={0.3}
            color={formData.name ? (darkMode ? 0x000000 : 0x333333) : (darkMode ? 0x888888 : 0x666666)}
            position={[-3.5, 1.5, 0.2]}
            anchorX="left"
            anchorY="middle"
            font="/Fonts/roc-grotesk-regular.otf"
            opacity={opacity}
          >
            {formData.name || "My name is"}
          </Text>

          {/* Email Field */}
          <Plane 
            args={[8, 0.8]} 
            position={[0, 0.3, 0.1]} 
            opacity={opacity}
            onClick={() => setActiveField('email')}
          >
            <meshBasicMaterial 
              color={activeField === 'email' ? (darkMode ? 0x000000 : 0xffffff) : (darkMode ? 0x333333 : 0xcbcbcb)} 
              transparent 
              opacity={0.8}
            />
          </Plane>
          
          {/* Email Field Border */}
          <Plane 
            args={[8.1, 0.9]} 
            position={[0, 0.3, 0.05]} 
            opacity={opacity}
          >
            <meshBasicMaterial 
              color={darkMode ? 0x666666 : 0x195787} 
              transparent 
              opacity={0.2}
            />
          </Plane>
          
          <Text
            fontSize={0.3}
            color={formData.email ? (darkMode ? 0x000000 : 0x333333) : (darkMode ? 0x888888 : 0x666666)}
            position={[-3.5, 0.3, 0.2]}
            anchorX="left"
            anchorY="middle"
            font="/Fonts/roc-grotesk-regular.otf"
            opacity={opacity}
          >
            {formData.email || "My e-mail is"}
          </Text>

          {/* Message Field */}
          <Plane 
            args={[8, 1.8]} 
            position={[0, -1.5, 0.1]} 
            opacity={opacity}
            onClick={() => setActiveField('message')}
          >
            <meshBasicMaterial 
              color={activeField === 'message' ? (darkMode ? 0x000000 : 0xffffff) : (darkMode ? 0x333333 : 0xcbcbcb)} 
              transparent 
              opacity={0.8}
            />
          </Plane>
          
          {/* Message Field Border */}
          <Plane 
            args={[8.1, 1.9]} 
            position={[0, -1.5, 0.05]} 
            opacity={opacity}
          >
            <meshBasicMaterial 
              color={darkMode ? 0x666666 : 0x195787} 
              transparent 
              opacity={0.2}
            />
          </Plane>
          
          <Text
            fontSize={0.3}
            color={formData.message ? (darkMode ? 0x000000 : 0x333333) : (darkMode ? 0x888888 : 0x666666)}
            position={[-3.5, -0.8, 0.2]}
            anchorX="left"
            anchorY="top"
            font="/Fonts/roc-grotesk-regular.otf"
            opacity={opacity}
            maxWidth={7}
          >
            {formData.message || "I'd love to work with you.."}
          </Text>

          {/* Submit Button */}
          <Plane 
            args={[3, 0.8]} 
            position={[0, -3.2, 0.1]} 
            opacity={opacity}
            onClick={handleSubmit}
          >
            <meshBasicMaterial 
              color={darkMode ? 0xffffff : 0x1b1b1b} 
              transparent 
              opacity={0.8}
            />
          </Plane>
          
          <Text
            fontSize={0.3}
            color={darkMode ? 0x333333 : 0xffffff}
            position={[0, -3.2, 0.2]}
            anchorX="center"
            anchorY="middle"
            font="/Fonts/roc-grotesk-bold.otf"
            opacity={opacity}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'sending...' : 'send message'}
          </Text>
        </group>
      )}

      {/* LinkedIn Button */}
      <Plane 
        args={[3, 0.8]} 
        position={[0, -4.2, 0.1]} 
        opacity={opacity}
        onClick={openLinkedIn}
      >
        <meshBasicMaterial 
          color={0x004467} 
          transparent 
          opacity={0.8}
        />
      </Plane>
      
      <Text
        fontSize={0.3}
        color={0xffffff}
        position={[0, -4.2, 0.2]}
        anchorX="center"
        anchorY="middle"
        font="/Fonts/roc-grotesk-bold.otf"
        opacity={opacity}
        onClick={openLinkedIn}
      >
        LinkedIn
      </Text>
    </group>
  );
} 